import classNames from 'classnames';
import React, { useMemo } from 'react';
import {
	IChatCommandWidgetCarouselData,
	IChatCommandWidgetMessage,
} from '@enuan/chat__utils';
import { useOnAction } from '../../widgets/useOnAction';
import { useMessageContext } from '../../message/message.context';
import { useBlockMessageClassNames } from 'src/hooks/core/messages.hooks';
import { Message } from '../../UI/Message/Message';
import { styled } from '@mui/material/styles';
import { useBreakpoint } from 'src/hooks/useBreakpoint';
import { Carousel } from '../../message/carousel/components/Carousel';
import type { IOptionalMessageProps } from '../../UI/Message/Message';

type ICarouselMessageContent =
	IChatCommandWidgetMessage<IChatCommandWidgetCarouselData>;

export const CarouselMessage = styled(
	React.forwardRef<HTMLElement, { className?: string } & IOptionalMessageProps>(
		({ className, ...messageProps }, ref) => {
			const { isMobile } = useBreakpoint();
			const { message } = useMessageContext<ICarouselMessageContent>();
			const messageClassName = useBlockMessageClassNames();
			const {
				_data: { parameters: { data } = {} },
				metadata: { id },
			} = message;
			const onAction = useOnAction(id);

			const carouselContent = useMemo(
				() => (
					<Carousel
						items={data?.items ?? []}
						settings={data?.settings}
						onAction={onAction}
					/>
				),
				[data?.items, data?.settings, onAction],
			);

			return (
				<Message
					{...messageProps}
					ref={ref}
					message={message}
					className={classNames(
						className,
						messageClassName,
						'message--carousel',
						isMobile && 'message--carousel-mobile',
					)}
					customHeader={null}
					customFooter={null}
					customAvatar={null}
					wrapBalloon={false}
				>
					{carouselContent}
				</Message>
			);
		},
	),
	{ label: 'CarouselMessage', target: 'CarouselMessage-root' },
)({
	display: 'grid',
	marginTop: '0.175em',
	marginLeft: 'calc(30px * 0.5)',
	marginRight: 'calc(30px * 0.75)',
	'& .carousel': {
		gridArea: 'main',
		gridColumn: '1 / -1',
		width: '100%',
	},
	'&:not(.message--carousel-mobile) .carousel .slick-slide': {
		transition: 'transform 0.2s',
		'&:hover': {
			transform: 'scale(1)',
		},
	},
	'&.message--carousel-mobile': {
		padding: 0,
		marginLeft: 0,
		marginRight: 0,
		'& .carousel': {
			maxWidth: '100%',
			marginLeft: 0,
			marginRight: 0,
			width: '100%',
		},
	},
});
